/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Box, ButtonBase, LinearProgress, Typography } from '@mui/material';
import type { Ref } from 'react';
import { formatPercent, type SimProgressPhase, TERMINAL_PHASES } from './progress';
import { getPillSx, getTrackSx, PHASE_TONE, tabularSx } from './simProgressStyles';

const PHASE_ICON = {
  starting: <SyncRoundedIcon color="primary" />,
  running: <BoltRoundedIcon color="secondary" />,
  done: <CheckCircleOutlineRoundedIcon color="success" />,
  failed: <ErrorOutlineRoundedIcon color="error" />,
  cancelled: <DoNotDisturbOnOutlinedIcon color="action" />,
};

interface Props {
  phase: Exclude<SimProgressPhase, 'hidden'>;
  overall: number | null;
  label: string;
  isOpen: boolean;
  onClick: () => void;
  ref?: Ref<HTMLButtonElement>;
}

/** Top-bar pill with the overall progress of the user's running simulations. */
export function SimProgressPill({ phase, overall, label, isOpen, onClick, ref }: Readonly<Props>) {
  const tone = PHASE_TONE[phase];
  // Finished runs fill the bar; only running progress is estimated.
  const value = TERMINAL_PHASES.includes(phase) ? 1 : overall;
  const isDeterminate = phase !== 'starting' && value !== null;
  return (
    <ButtonBase
      ref={ref}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-label={`Simulations: ${label}${isDeterminate && value !== null ? `, ${formatPercent(value)}` : ''}`}
      data-testid="sim-progress-pill"
      sx={(theme) => getPillSx(theme, tone)}
    >
      {PHASE_ICON[phase]}
      <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ width: 96 }}>
        <LinearProgress
          variant={isDeterminate ? 'determinate' : 'indeterminate'}
          value={isDeterminate && value !== null ? value * 100 : undefined}
          aria-hidden
          sx={(theme) => getTrackSx(theme, tone)}
        />
      </Box>
      {phase === 'failed' ? (
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'error.main' }}>
          View
        </Typography>
      ) : (
        isDeterminate &&
        value !== null &&
        phase !== 'cancelled' && (
          <Typography
            component="span"
            sx={{ ...tabularSx, fontSize: 11, color: 'text.secondary', minWidth: 28 }}
          >
            {formatPercent(value)}
          </Typography>
        )
      )}
    </ButtonBase>
  );
}
